import React, { FC, useState } from 'react';
import { Button, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select } from '@mui/material';
import { SendOutlined, DownloadOutlined } from '@mui/icons-material';
import { useAppDispatch } from '@hooks/redux-custom';
import { LoadingButton } from '@mui/lab';
import { showToast } from '@store/slices/notification';
import { Order, InvoiceHandler } from '@models';
import {
  useGetOrderProductsQuery,
  useGetShippingAddressQuery,
  useGetMetafieldsByOrderIdQuery,
  useHandleInvoiceMutation,
  useGetCustomerSessionDataQuery,
} from '@services';
import OrderRefundModal from '../OrderRefundModal/OrderRefundModal';
import OrderInvoice from './OrderInvoice/OrderInvoice';
import { useAppSelector } from '@hooks/redux-custom';
import OverViewLoading from './OverViewLoading';

type Props = {
  order: Order;
  hideRefund?: boolean;
};

const LOYALTY_CODE = process.env.LOYALTY_CODE || 'loyal';
const VOUCHER_EARNED_RULE_NAME = process.env.VOUCHER_EARNED_RULE_NAME || 'Voucher Earned';
const ELIGIBLE_ORDER_RULE_NAME = process.env.ELIGIBLE_ORDER_RULE_NAME || 'Eligible Order';
const VOUCHER_VALUE_PATH_NAME = process.env.VOUCHER_VALUE_PATH_NAME || 'voucher_value';
const LOYALTY_ORDER_COUNT_PATH_NAME = process.env.LOYALTY_ORDER_COUNT_PATH_NAME || 'loyalty_order_count';

export const OrderView: FC<Props> = ({ order, hideRefund = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefundDownloading, setIsRefundDownloading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isRefundSending, setIsRefundSending] = useState(false);
  const { data: orderProducts } = useGetOrderProductsQuery(order.id);
  const { data: talonOneSession } = useGetCustomerSessionDataQuery({ sessionID: order.cart_id });
  const { data: orderShippingAddress, isLoading: isAddressLoading } = useGetShippingAddressQuery(order.id);
  const { data: orderMetaFields, isLoading: isMetaLoading } = useGetMetafieldsByOrderIdQuery({ orderId: order.id });
  const orderStatus = useAppSelector((state) => state.order?.value?.orderStatus);
  const customer = useAppSelector((state) => state.customer.value.selectedCustomer);
  const dispatch = useAppDispatch();

  const [handleInvoice] = useHandleInvoiceMutation();

  const activeUser = useAppSelector((state) => state.activeUser.value);

  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'customer');
  const hasWriteAccess = userAccessStatus.role > 1;

  const anyActionInProgress = isRefundDownloading || isRefundSending || isDownloading || isSending;

  const openRefundModal = (order: Order) => {
    if (order) {
      setIsModalOpen(true);
    }
  };

  const refundEnableIds: Array<number> = [];

  orderStatus?.map((item: any) => {
    if (
      item.name.trim().toLowerCase() === 'cancelled' ||
      item.name.trim().toLowerCase() === 'completed' ||
      item.name.trim().toLowerCase() === 'partially refunded'
    )
      refundEnableIds.push(item.id);

    return refundEnableIds;
  });

  const canPerformRefund = refundEnableIds.includes(order?.status_id) && hasWriteAccess && Number(order.total_inc_tax) !== 0;

  const refundedIds: number[] =
    orderStatus
      ?.filter((item: any) => ['refunded', 'partially refunded'].includes(item.name.trim().toLowerCase()))
      .map((item: any) => item.id) || [];

  const isOrderRefunded = refundedIds.includes(order?.status_id);

  const resendInvoice = () => {
    setIsSending(true);
    const payload: InvoiceHandler = {
      orderId: order.id,
      customerId: customer?.id,
    };

    handleInvoice(payload)
      .then((data: any) => {
        setIsSending(false);

        if (data) {
          dispatch(showToast({ message: 'Invoice sent successfully', type: 'success' }));
        }
      })
      .catch((err) => {
        setIsSending(false);
        console.log(err);
        dispatch(showToast({ message: 'Error while sending invoice, contact Tech team', type: 'error' }));
      });
  };

  const resendRefundInvoice = () => {
    setIsRefundSending(true);
    const payload: InvoiceHandler = {
      orderId: order.id,
      customerId: customer?.id,
      isRefundInvoice: true,
    };

    handleInvoice(payload)
      .then((data: any) => {
        setIsRefundSending(false);

        if (data) {
          dispatch(showToast({ message: 'Refund invoice sent successfully', type: 'success' }));
        }
      })
      .catch((err) => {
        setIsRefundSending(false);
        console.log(err);
        dispatch(showToast({ message: 'Error while sending refund invoice, contact Tech team', type: 'error' }));
      });
  };

  const downloadInvoice = () => {
    setIsDownloading(true);
    const payload: InvoiceHandler = {
      orderId: order.id,
      customerId: customer?.id,
      skipEmailSend: true,
    };

    handleInvoice(payload)
      .then((data: any) => {
        setIsDownloading(false);
        window.open(data.data.url, '_self');
      })
      .catch((err) => {
        setIsDownloading(false);
        console.log(err);
        dispatch(showToast({ type: 'error', message: `Problem downloading the Invoice, contact Tech team` }));
      });
  };

  const getLoyaltyData = () => {
    if (!talonOneSession || !talonOneSession.effects) {
      return {
        loyalty_voucher_value: '',
        loyalty_order_count: '',
        loyalty_coupon: '',
      };
    }

    return talonOneSession.effects.reduce(
      (loyaltyDetails, effect) => {
        if (!effect.props?.value) {
          return loyaltyDetails;
        }

        if (effect.ruleName?.includes(ELIGIBLE_ORDER_RULE_NAME) && effect.props.path?.includes(VOUCHER_VALUE_PATH_NAME)) {
          loyaltyDetails.loyalty_voucher_value = effect.props.value;
        }

        if (effect.ruleName?.includes(ELIGIBLE_ORDER_RULE_NAME) && effect.props.path?.includes(LOYALTY_ORDER_COUNT_PATH_NAME)) {
          loyaltyDetails.loyalty_order_count = effect.props.value;
        }

        if (effect.ruleName?.includes(VOUCHER_EARNED_RULE_NAME) && String(effect.props.value).toLocaleLowerCase().includes(LOYALTY_CODE)) {
          loyaltyDetails.loyalty_coupon = effect.props.value;
        }

        return loyaltyDetails;
      },
      {
        loyalty_voucher_value: '',
        loyalty_order_count: '',
        loyalty_coupon: '',
      },
    );
  };

  const downloadRefundInvoice = () => {
    setIsRefundDownloading(true);
    const payload: InvoiceHandler = {
      orderId: order.id,
      customerId: customer?.id,
      skipEmailSend: true,
      isRefundInvoice: true,
    };

    handleInvoice(payload)
      .then((data: any) => {
        setIsRefundDownloading(false);
        window.open(data.data.url, '_self');
      })
      .catch((err) => {
        setIsRefundDownloading(false);
        console.log(err);
        dispatch(showToast({ type: 'error', message: `Problem downloading the refund invoice, contact Tech team` }));
      });
  };

  const downloadRefundOptions = [
    { label: 'Download Invoice', handler: downloadInvoice },
    { label: 'Download Refund Invoice', handler: downloadRefundInvoice },
  ];

  const sendRefundOptions = [
    { label: 'Resend Invoice', handler: resendInvoice },
    { label: 'Send Refund Invoice', handler: resendRefundInvoice },
  ];

  const renderDropdown = (
    options: Array<{ label: string; handler: () => void }>,
    loading: boolean,
    testId: string,
    dropdownLabel: string,
  ) => {
    return (
      <FormControl fullWidth>
        <InputLabel>{dropdownLabel}</InputLabel>
        <Select
          label={dropdownLabel}
          onChange={(event) => {
            const selectedOption = options.find((option) => option.label === event.target.value);

            if (selectedOption) {
              selectedOption.handler();
            }
          }}
          disabled={loading}
          data-testid={testId}
          style={{ width: '200px' }}
          size="medium"
        >
          {options.map((option) => (
            <MenuItem key={option.label} value={option.label}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <>
      {anyActionInProgress && <LinearProgress sx={{ my: 1 }} />}
      <Grid container my={2} px={2} justifyContent="flex-end" spacing={2}>
        {canPerformRefund && !hideRefund && (
          <Grid item>
            <Button variant="contained" onClick={() => openRefundModal(order)} data-testid="order-refund-btn">
              Refund
            </Button>
          </Grid>
        )}
        {order.status_id > 1 && ( // only show invoice button if order is not in pending or incomplete state
          <>
            <Grid item>
              {isOrderRefunded ? ( // show 'send refund invoice' if the order is refunded
                renderDropdown(sendRefundOptions, isRefundSending, 'order-refund-send-dropdown', 'Send Invoice')
              ) : (
                <LoadingButton
                  loading={isSending}
                  variant="contained"
                  color="primary"
                  endIcon={<SendOutlined />}
                  onClick={resendInvoice}
                  data-testid="order-resend-invoice-btn"
                >
                  Resend Invoice
                </LoadingButton>
              )}
            </Grid>
            <Grid item>
              {isOrderRefunded ? ( // show 'download refund invoice' if the order is refunded
                renderDropdown(downloadRefundOptions, isRefundDownloading, 'order-download-dropdown', 'Download Invoice')
              ) : (
                <LoadingButton
                  loading={isDownloading}
                  variant="contained"
                  color="primary"
                  endIcon={<DownloadOutlined />}
                  onClick={downloadInvoice}
                  data-testid="order-download-invoice-btn"
                >
                  Download Invoice
                </LoadingButton>
              )}
            </Grid>
          </>
        )}
      </Grid>
      {isMetaLoading || isAddressLoading ? (
        <OverViewLoading />
      ) : (
        <OrderInvoice
          order={order}
          orderProducts={orderProducts ?? []}
          shippingAddress={orderShippingAddress ?? []}
          metaFields={orderMetaFields}
          loyalty={getLoyaltyData()}
        />
      )}
      <OrderRefundModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        order={order}
        orderProducts={orderProducts ?? []}
        orderShippingAddress={orderShippingAddress}
      />
    </>
  );
};
